import { Component, OnInit, AfterViewChecked, ElementRef, Renderer2, ChangeDetectorRef, HostBinding } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChatbotService } from '../../../../services/chatbot-services/chatbot.service';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationService } from '../../../../services/configuration/configuration.service';
import { MetadataConfigService, MetadataFieldConfig } from '../../../../services/metadata-config/metadata-config.service';
import { LoggerService } from 'src/services/logger-services/logger.service';
import { extractRetrievalModeFromHistoryAnswer } from '../../../../shared/utils/retrieval-mode.util';
import { formatAttachmentDisplayName } from '../../../../shared/utils/attachment-display-name.util';
import {
  DOC_TABLE_CELL_PLACEHOLDER,
  formatDocReleaseDate,
  formatDocTableCell,
  getRegulationDocumentRows,
  RegulationDocumentRow,
} from '../../../../shared/utils/regulation-document-rows.util';
import { NewsItem } from '../../../models/news-updates.models';
import { AgentConfigService } from '../../services/agent-config/agent-config.service';
import { GetSharedChatService } from '../../services/agent-threads/get-shared-chat/get-shared-chat.service';
import { environment } from 'src/environments/environment';
import {
  MetadataCategoryDisplay,
  resolveMetadataCategoryDisplay,
} from '../../../../shared/utils/metadata-category.util';
import { getSharedChatErrorMessageKey } from '../../../../shared/utils/shared-chat-error.util';

/**
 * Component for viewing shared agent chat conversations
 * Handles display of agent chat history, metadata, visualizations, and news updates
 * Supports JWT token-based sharing with proper authentication
 * Used for regulations and Via agents
 */
@Component({
  selector: 'app-agent-share-chat-viewer',
  templateUrl: './agent-share-chat-viewer.component.html',
  styleUrls: ['./agent-share-chat-viewer.component.scss']
})
export class AgentShareChatViewerComponent implements OnInit, AfterViewChecked {
  /** Array of chat data objects containing questions, answers, and associated metadata */
  chatData: {
    /** The user's question */
    question: string;
    /** The chatbot's response */
    answer: string;
    /** Timestamp when the chat was created */
    created_date: string;
    /** Image visualization data for charts/graphs */
    image_visualization: any;
    /** Visualization configuration data */
    visualization: any;
    /** Array of suggested follow-up questions */
    followup_questions: string[];
    /** Metadata associated with the response */
    metadata: any;
    /** Optional news and updates related to the response */
    newsAndUpdates?: NewsItem[];
    /** ID of associated news feeds */
    news_feeds_id: number | null;
    /** Retrieval mode used for this response, when present in history */
    retrieval_mode?: 'latest' | 'all';
  }[] = [];
  
  /** Loading state indicator */
  loading: boolean = true;
  
  /** i18n key for the error message shown to the user */
  error: string = '';
  
  /** Name of the shared chat session */
  sessionName: string = '';
  
  /** Flag indicating if news and feeds functionality is enabled */
  isNewsAndFeedsEnabled: boolean = false;

  /** Carousel state tracking for metadata display pagination */
  metadataCarouselStates: { [key: number]: number } = {};
  
  /** Number of metadata items to display per page in carousel */
  itemsPerPage: number = 3;
  
  /** Tenant identifier extracted from share token */
  tenantId: string = '';
  
  /** Thread identifier for the shared conversation */
  threadId: string = '';
  
  /** Response identifier for single response sharing */
  responseId: string = '';

  /** Agent ID for the shared chat */
  agentId: string = '';

  /** Enables RIA rich-table styling via :host-context(.ria-agent) in markdown-rich-table. */
  @HostBinding('class.ria-agent') get isRiaAgent(): boolean {
    return this.agentId === 'ria_agent';
  }

  /** Enables fixed-viewport rich tables for regulations. */
  @HostBinding('class.regulations-agent') get isRegulationsAgentHost(): boolean {
    return this.agentId === 'regulations_agent';
  }

  /** Fusion App UI base URL for regulation hyperlinks in markdown tables */
  fusionAppUiUrl = '';

  /** User ID extracted from share token */
  userId: string = '';

  /** Flag to track if view has been initialized to prevent unnecessary DOM updates */
  private viewInitialized: boolean = false;

  /** Flag to track if content has been processed to avoid duplicate processing */
  private contentProcessed: boolean = false;

  /** 
   * Error formatting function for consistent error logging
   * @private
   */
  private readonly AgentShareChatViewerError = (error: any) =>
    `[AgentShareChatViewer] - ${JSON.stringify(error)}`

  /**
   * Constructor for AgentShareChatViewerComponent
   * Injects required services for agent chat viewing functionality
   * 
   * @param chatbotService - Service for chatbot API interactions
   * @param route - Angular router service for accessing route parameters
   * @param elementRef - Reference to the component's DOM element
   * @param renderer - Angular renderer for DOM manipulation
   * @param languageService - Service for handling language changes
   * @param translate - NgX Translate service for internationalization
   * @param cdr - Change detector reference for manual change detection
   * @param configService - Configuration service for app settings
   * @param metadataConfigService - Service for metadata field configuration
   * @param logger - Logging service for error tracking
   * @param agentConfigService - Service for agent configuration
   * @param getSharedChatService - Service for fetching shared chat data
   */
  constructor(
    public chatbotService: ChatbotService,
    public route: ActivatedRoute,
    public elementRef: ElementRef,
    public renderer: Renderer2,
    public languageService: LanguageChangeService,
    public translate: TranslateService,
    public cdr: ChangeDetectorRef,
    public configService: ConfigurationService,
    public metadataConfigService: MetadataConfigService,
    public logger: LoggerService,
    public agentConfigService: AgentConfigService,
    public getSharedChatService: GetSharedChatService
  ) { }

  /**
   * Angular lifecycle hook - component initialization
   * Initializes the component by:
   * - Checking if news and feeds functionality is enabled
   * - Setting up language configuration
   * - Extracting share token parameters
   * - Loading shared chat data
   */
  ngOnInit(): void {
    // Check if news and feeds functionality is enabled
    this.configService.isNewsAndFeedsEnabled().then((enabled) => {
      this.isNewsAndFeedsEnabled = enabled;
    }).catch((error) => {
      this.logger.logError(this.AgentShareChatViewerError(error), "isNewsAndFeedsEnabled");
    });

    this.configService.loadConfiguration().then((config) => {
      this.fusionAppUiUrl = (config as { FusionAppUIUrl?: string })?.FusionAppUIUrl ?? '';
    }).catch((error) => {
      this.logger.logError(this.AgentShareChatViewerError(error), 'loadConfiguration');
    });

    // Fetch the current language from local storage
    const currentLanguage: string = localStorage.getItem('language') || 'en';
    this.route.params.subscribe(params => {
      const shareToken = params['token'];
      if (shareToken) {
        const payload = this.decodeJwtPayload(shareToken);
        this.tenantId = payload.tenant_id || this.route.snapshot.queryParamMap.get('tenant_id');
        this.threadId = payload.thread_id || this.route.snapshot.queryParamMap.get('thread_id');
        // Centralized agent-common tokens carry `message_id`; older share tokens may
        // carry `response_id`. Both name the shared turn, so either is accepted.
        this.responseId = payload.message_id
          || payload.response_id
          || this.route.snapshot.queryParamMap.get('message_id')
          || this.route.snapshot.queryParamMap.get('response_id');
        this.agentId = payload.agent_id || this.route.snapshot.queryParamMap.get('agent_id') || 'regulations_agent';
        // Extract user_id from token, query params, or use environment LoginUserID as fallback
        this.userId = payload.user_id || this.route.snapshot.queryParamMap.get('user_id') || environment.LoginUserID || '';
        this.loadShare_api_response(shareToken, this.tenantId, this.threadId, this.responseId);
      } else {
        this.setSharedChatLoadError();
      }
    });
    this.translate.use(currentLanguage).subscribe(() => {
      // Additional actions can be performed here if needed
    }, (error) => {
      this.logger.logError(this.AgentShareChatViewerError(error), "translate.use");
    });
  }

  /**
   * Get agent icon path with fallback
   * @returns Agent icon URL or default chatbot icon
   */
  get agentIcon(): string {
    try {
      const agent = this.agentConfigService.getAgentConfig(this.agentId);
      return agent?.icon || '../../assets/images/chatbot-icon.svg';
    } catch (error: any) {
      this.logger.logInfo('[AgentShareChatViewer] Error getting agent icon', 'agentIcon', error);
      return '../../assets/images/chatbot-icon.svg';
    }
  }

  /**
   * Gets the truncated session name for display
   * Truncates to 80 characters and adds ellipsis if longer
   * 
   * @returns Truncated session name with ellipsis if needed
   */
  get displaySessionName(): string {
    const maxLength = 80;
    if (this.sessionName && this.sessionName.length > maxLength) {
      return this.sessionName.slice(0, maxLength) + '...';
    }
    return this.sessionName;
  }

  /**
   * Decodes JWT token payload to extract sharing parameters
   * 
   * @param token - JWT token string to decode
   * @returns Decoded payload object containing tenant_id, thread_id, response_id, agent_id
   */
  public decodeJwtPayload(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decodeURIComponent(escape(decoded)));
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "decodeJwtPayload");
      return {};
    }
  }

  /**
   * Transforms new API chat history format to legacy format for compatibility
   * Handles parsing of answers, follow-up questions, and metadata structure
   * 
   * @param newData - Array of chat history items in new API format
   * @returns Promise<any[]> - Array of transformed chat history items in legacy format
   */
  async transformNewChatHistoryToOldFormat(newData: any[]): Promise<any[]> {
    try {
      return newData.map((item: any) => {
        const sourceMetadata = item?.source_metadata || item?.answer?.source_metadata || {};
        // Parse the answer if it's a JSON string
        let parsedAnswer = item?.answer || '';
        let image_visualization = [];
        if (typeof item.answer === 'string') {
          try {
            const answerObj = JSON.parse(item.answer);
            parsedAnswer = answerObj.text || item?.answer || '';
            image_visualization = answerObj?.image_visualization || [];
          } catch (e) {
            // If parsing fails, use the original answer
            parsedAnswer = item?.answer || '';
            image_visualization = item?.image_visualization || [];
          }
        } else if (item.answer && typeof item.answer === 'object') {
          parsedAnswer = item.answer.text || '';
          image_visualization = item.answer.image_visualization || [];
        }

        const retrieval_mode = extractRetrievalModeFromHistoryAnswer(item.answer);

        // Extract follow-up questions from metadata
        let formattedFollowUpQuestions = [];
        let savedMetadata: Record<string, unknown> = {};
        try {
          const parsedMetadata: unknown = JSON.parse(item.metadata || '{}');
          if (parsedMetadata && typeof parsedMetadata === 'object' && !Array.isArray(parsedMetadata)) {
            savedMetadata = parsedMetadata as Record<string, unknown>;
          }
          if (Array.isArray(savedMetadata['follow_up_q'])) {
            // Structure: follow_up_q with objects [{Question: "..."}, {Question: "..."}]
            formattedFollowUpQuestions = savedMetadata['follow_up_q'].map((q: any) => q.Question);
          } else if (Array.isArray(savedMetadata['follow_up_questions'])) {
            // Structure: follow_up_questions with direct strings ["question1", "question2"]
            formattedFollowUpQuestions = savedMetadata['follow_up_questions'];
          }
        } catch (e) {
          this.logger.logError(this.AgentShareChatViewerError(e), "transformNewChatHistoryToOldFormat");
          formattedFollowUpQuestions = [];
        }

        const resolvedReferences = sourceMetadata.references;
        const followUpQuestions = sourceMetadata.follow_up_questions || formattedFollowUpQuestions;
        const shareCardMetadata = Array.isArray(resolvedReferences) && resolvedReferences.length > 0
          ? resolvedReferences[0]
          : (resolvedReferences || {});

        // Create metadata object in old format
        const metadataObj: Record<string, unknown> = {
          follow_up_q: followUpQuestions,
          IsInternet: false,
          module_name: item?.module_name || 'regulations',
          answer: {
            metadata: shareCardMetadata,
            text: parsedAnswer,
            image_visualization: image_visualization
          }
        };
        metadataObj['context'] = savedMetadata['context'];
        metadataObj['active_identifiers'] = savedMetadata['active_identifiers'];

        return {
          pk_chathistoryid: item?.response_id || item?.id,
          sessionid: item?.thread_id || '',
          module_name: item?.module_name || '',
          chatname: item?.chat_name || '',
          question: item?.question,
          answer: parsedAnswer,
          retrieval_mode,
          islike: item?.is_like || false,
          comments: item?.comments || '',
          feedback: item?.feedback || '',
          additionalcomments: '',
          metadata: JSON.stringify(metadataObj),
      modified_date: this.format_date_time(item?.created_at),
        };
      });
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "transformNewChatHistoryToOldFormat");
      return [];
    }

  }


  format_date_time(parsed_date: Date) {
    try {
      // Keep the date in UTC without timezone conversion
      const date = new Date(parsed_date);

      const day = date.getUTCDate().toString().padStart(2, '0'); // Use UTC date
      const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }); // Use UTC month
      const year = date.getUTCFullYear(); // Use UTC year
      const hours = date.getUTCHours().toString().padStart(2, '0'); // Use UTC hours in 24-hour format
      const minutes = date.getUTCMinutes().toString().padStart(2, '0'); // Use UTC minutes

      const formattedDate = `${month} ${day}, ${year}, ${hours}:${minutes} UTC`;
      return formattedDate;
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "format_date_time");
      return "";
    }
  }
  /**
   * Loads shared chat data using the appropriate API endpoint
   * Determines whether to load a single response or entire thread based on available parameters
   * 
   * @param shareToken - JWT token for authentication
   * @param tenantId - Tenant identifier
   * @param threadId - Thread identifier (for full conversation sharing)
   * @param responseId - Response identifier (for single response sharing)
   */
  async loadShare_api_response(shareToken: string, tenantId: string, threadId: string, responseId: string) {
    try {
      if (tenantId && responseId) {
        // Single response sharing
        this.getSharedChatService.getSharedConversation(this.agentId, tenantId, responseId, shareToken, this.userId)
          .subscribe({
            next: (response: any) => {
              this.loadSharedChat(response);
            },
            error: (err: { status?: number }) => {
              this.logger.logError(
                this.AgentShareChatViewerError(err),
                'loadShare_api_response - getSharedConversation'
              );
              this.setSharedChatLoadError(err?.status);
            }
          });
      } else if (tenantId && threadId) {
        // Full thread sharing
        this.getSharedChatService.getSharedThread(this.agentId, tenantId, threadId, shareToken, this.userId)
          .subscribe({
            next: (response: any) => {
              this.loadSharedChat(response);
            },
            error: (err: { status?: number }) => {
              this.logger.logError(
                this.AgentShareChatViewerError(err),
                'loadShare_api_response - getSharedThread'
              );
              this.setSharedChatLoadError(err?.status);
            }
          });
      }
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "loadShare_api_response");
      this.setSharedChatLoadError();
      return;
    }
  }

  private setSharedChatLoadError(status?: number): void {
    this.error = getSharedChatErrorMessageKey(status);
    this.loading = false;
  }

  /**
   * Processes and loads shared chat data from API response
   * Transforms data structure, extracts metadata, and sets up UI state
   * 
   * @param response - API response containing shared chat data
   */
  async loadSharedChat(response: any) {
    try {
      if (response.status === 200) {
        // Extract session name from response - will be updated to use question after data is loaded
        const threadName = response.body?.thread_name || '';
        const responseData = await this.transformNewChatHistoryToOldFormat(response.body?.data.reverse() || []);
        // Transform the response data to the required structure
        this.chatData = await Promise.all(responseData.map(async (item: any) => {
          let followupQuestions: string[] = [];
          let parsedMetadata: any = {};
          let image_visualization: any = [];
          let visualization: any = [];
          let newsAndUpdates: NewsItem[] = [];
          let news_and_feeds_id: number | null = null;

          // Parse metadata to extract follow_up_questions, visualization, and news data
          try {
            const metadata = JSON.parse(item.metadata);

            // Handle both follow-up question structures
            if (metadata.follow_up_q && Array.isArray(metadata.follow_up_q)) {
              // Handle both string arrays and object arrays
              if (typeof metadata.follow_up_q[0] === 'string') {
                // Structure: follow_up_q with direct strings ["question1", "question2"] (from transformed format)
                followupQuestions = metadata.follow_up_q;
              } else {
                // Structure: follow_up_q with objects [{Question: "..."}, {Question: "..."}] (from original format)
                followupQuestions = metadata.follow_up_q.map((q: any) => q.Question);
              }
            } else if (metadata.follow_up_questions && Array.isArray(metadata.follow_up_questions)) {
              // Structure: follow_up_questions with direct strings ["question1", "question2"]
              followupQuestions = metadata.follow_up_questions;
            } else {
              followupQuestions = [];
            }

            if (metadata.answer && metadata.answer.image_visualization) {
              image_visualization = await this.extractImageVisualization(item.metadata);
            }

            // Extract visualization data
            if (metadata.answer && metadata.answer.visualization && Array.isArray(metadata.answer.visualization)) {
              visualization = metadata.answer.visualization;
            }

            newsAndUpdates = item?.news_and_feeds?.news_and_feeds_data || [];
            news_and_feeds_id = item?.news_and_feeds?.news_and_feeds_id || null;
            parsedMetadata = metadata;
          } catch (e) {
            this.logger.logError(this.AgentShareChatViewerError(e), "loadSharedChat");
            followupQuestions = [];
            parsedMetadata = {};
            newsAndUpdates = [];
            news_and_feeds_id = null;
            image_visualization = [];
          }
          return {
            question: item.question,
            answer: item.answer,
            created_date: item.modified_date,
            image_visualization: image_visualization,
            visualization: visualization,
            followup_questions: followupQuestions,
            metadata: parsedMetadata,
            retrieval_mode: item.retrieval_mode,
            newsAndUpdates: newsAndUpdates,
            news_feeds_id: news_and_feeds_id
          };
        }));

        // Set session name to the first question, fallback to thread name if no question
        this.sessionName = threadName;

        this.resetCarousel(); // Reset carousel for new data
        this.loading = false;
        
        // Reset content processed flag to allow processing of new content
        this.contentProcessed = false;
        
        // Trigger change detection to process the new content
        this.cdr.detectChanges();
      } else {
        this.setSharedChatLoadError(response?.status);
      }
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "loadSharedChat");
      return;
    }
  }

  /**
   * Checks if a metadata item is blocked and should not be displayed
   * An item is considered blocked if it only contains system properties
   * 
   * @param item - Metadata item to check
   * @returns boolean - True if item is blocked, false otherwise
   */
  isItemBlocked(item: any): boolean {
    try {
      if (!item || typeof item !== 'object') {
        return false;
      }

      // Check if item has blocked property set to true
      if (item.blocked === true) {
        // Check if this item only contains blocked and potentially key/index properties
        const keys = Object.keys(item);
        const allowedKeys = ['blocked', 'key', 'index'];
        const hasOnlyBlockedOrSystemKeys = keys.every(key => allowedKeys.includes(key));

        if (hasOnlyBlockedOrSystemKeys) {
          return true;
        }
      }

      return false;
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "isItemBlocked");
      return false;
    }
  }

  /**
   * Extracts metadata items as an array, filtering out blocked items
   * Handles both new array structure and legacy numbered key structure
   * 
   * @param metadata - Metadata object containing answer and metadata properties
   * @returns any[] - Array of non-blocked metadata items
   */
  getMetadataItems(metadata: any): any[] {
    try {
      if (!metadata || !metadata.answer || !metadata.answer.metadata) {
        return [];
      }

      const items: any[] = [];
      const metadataObj = metadata.answer.metadata;

      // Handle new structure (array of items) or old structure (numbered keys)
      if (Array.isArray(metadataObj)) {
        // New structure: metadata is already an array
        metadataObj.forEach(item => {
          if (!this.isItemBlocked(item)) {
            items.push(item);
          }
        });
      } else {
        // Old structure: extract numbered metadata items (1, 2, 3, etc.)
        Object.keys(metadataObj).forEach(key => {
          if (!isNaN(Number(key))) { // Check if key is a number
            const item = metadataObj[key];
            // Only add if not blocked
            if (!this.isItemBlocked(item)) {
              items.push(item);
            }
          }
        });
      }
      return items;
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "getMetadataItems");
      return [];
    }
  }

  /**
   * Extracts country information from metadata items
   * Handles multiple data structures for backwards compatibility
   * 
   * @param item - Metadata item to extract countries from
   * @returns string[] - Array of country names
   */
  extractCountries(item: any): string[] {
    try {
      // Handle new metadata structure with 'metadata.countries' array
      if (item.metadata && item.metadata.countries && Array.isArray(item.metadata.countries)) {
        return item.metadata.countries;
      }

      // Handle old structure with 'countries' array directly on item
      if (item.countries && Array.isArray(item.countries)) {
        return item.countries;
      }

      // Handle old structure with 'identifiers' array
      if (item.identifiers && Array.isArray(item.identifiers)) {
        const countries: string[] = [];
        item.identifiers.forEach((identifier: any) => {
          if (identifier.Country && Array.isArray(identifier.Country)) {
            countries.push(...identifier.Country);
          }
        });
        return countries;
      }

      // Handle single country (legacy support)
      if (item.Country && typeof item.Country === 'string') {
        return [item.Country];
      }

      // Handle country field accessed through metadataConfigService with different possible field names
      const possibleCountryFields = ['countries', 'country', 'metadata.countries', 'Country'];
      for (const fieldName of possibleCountryFields) {
        const countryFromField = this.getFieldValue(item, fieldName);
        if (countryFromField) {
          if (Array.isArray(countryFromField)) {
            return countryFromField;
          } else if (typeof countryFromField === 'string') {
            return [countryFromField];
          }
        }
      }

      return [];
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "extractCountries");
      return [];
    }
  }

  /**
   * Formats country list for display, showing first two countries and count of remaining
   * 
   * @param item - Metadata item containing country information
   * @returns Object with firstTwo countries as string and remainingCount as number
   */
  displayCountries(item: any): { firstTwo: string, remainingCount: number } {
    try {
      // For all modules, use the extractCountries method to handle different data structures
      const countries = this.extractCountries(item);

      if (!countries || !Array.isArray(countries)) {
        return { firstTwo: '', remainingCount: 0 };
      }

      const countryArray = countries.map(country => country.trim()) || [];
      const firstTwo = countryArray.slice(0, 2).join(', ');
      const remainingCount = Math.max(0, countryArray.length - 2);
      return { firstTwo, remainingCount };
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "displayCountries");
      return { firstTwo: '', remainingCount: 0 };
    }
  }

  /**
   * Resolves the single category label for metadata UI (shared rules).
   */
  getCategoryDisplay(item: unknown): MetadataCategoryDisplay | null {
    return resolveMetadataCategoryDisplay(item);
  }

  /**
   * Extracts brand name information from metadata items
   * Handles multiple data structures for backwards compatibility
   * 
   * @param item - Metadata item to extract brand names from
   * @returns string[] - Array of brand names
   */
  extractBrandNames(item: any): string[] {
    try {
      // Handle new metadata structure with 'metadata.brandNames' array
      if (item.metadata && item.metadata.brandNames && Array.isArray(item.metadata.brandNames)) {
        return item.metadata.brandNames;
      }

      // Handle old structure with 'brandNames' array directly on item
      if (item.brandNames && Array.isArray(item.brandNames)) {
        return item.brandNames;
      }

      // Handle single brand name (legacy support)
      if (item.brandName && typeof item.brandName === 'string') {
        return [item.brandName];
      }

      // Handle brand name field accessed through metadataConfigService with different possible field names
      const possibleBrandNameFields = ['brandNames', 'brandName', 'metadata.brandNames', 'metadata.brandName'];
      for (const fieldName of possibleBrandNameFields) {
        const brandNameFromField = this.getFieldValue(item, fieldName);
        if (brandNameFromField) {
          if (Array.isArray(brandNameFromField)) {
            return brandNameFromField;
          } else if (typeof brandNameFromField === 'string') {
            return [brandNameFromField];
          }
        }
      }

      return [];
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "extractBrandNames");
      return [];
    }
  }

  /**
   * Formats brand name list for display, showing first two brand names and count of remaining
   * 
   * @param item - Metadata item containing brand name information
   * @returns Object with firstTwo brand names as string and remainingCount as number
   */
  displayBrandNames(item: any): { firstTwo: string, remainingCount: number } {
    try {
      // Use the extractBrandNames method to handle different data structures
      const brandNames = this.extractBrandNames(item);

      if (!brandNames || !Array.isArray(brandNames)) {
        return { firstTwo: '', remainingCount: 0 };
      }

      const brandNameArray = brandNames.map(brandName => brandName.trim()) || [];
      const firstTwo = brandNameArray.slice(0, 2).join(', ');
      const remainingCount = Math.max(0, brandNameArray.length - 2);
      return { firstTwo, remainingCount };
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "displayBrandNames");
      return { firstTwo: '', remainingCount: 0 };
    }
  }

  /**
   * Determines module name from metadata for legacy support
   * Analyzes metadata structure to identify RTQ vs regulations module
   * 
   * @param metadata - Metadata object to analyze
   * @returns string - Module name identifier
   */
  getModuleNameFromMetadata(metadata: any): string {
    try {
      if (!metadata || !metadata.answer || !metadata.answer.metadata) {
        return '';
      }

      const metadataObj = metadata.answer.metadata;
      const firstItem = Object.keys(metadataObj).find(key => !isNaN(Number(key)));

      if (firstItem && metadataObj[firstItem]) {
        const item = metadataObj[firstItem];
        // Check if it has RTQ data
        if (item.rtq_id) {
          return 'view_access_to_rtq';
        }
        // Check if it has regulation data (default case)
        if (item.ins_id) {
          return 'view_access_to_regulations';
        }
      }

      return 'view_access_to_regulations'; // Default to regulations view
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "getModuleNameFromMetadata");
      return '';
    }
  }

  /**
   * Gets current carousel index for a specific chat item
   * 
   * @param chatItemIndex - Index of the chat item
   * @returns number - Current carousel position
   */
  getCurrentIndex(chatItemIndex: number): number {
    return this.metadataCarouselStates[chatItemIndex] || 0;
  }

  /**
   * Advances carousel to next page of metadata items
   * 
   * @param chatItemIndex - Index of the chat item whose carousel to advance
   */
  nextSlide(chatItemIndex: number): void {
    const currentIndex = this.getCurrentIndex(chatItemIndex);
    this.metadataCarouselStates[chatItemIndex] = currentIndex + this.itemsPerPage;
  }

  /**
   * Moves carousel to previous page of metadata items
   * 
   * @param chatItemIndex - Index of the chat item whose carousel to move back
   */
  prevSlide(chatItemIndex: number): void {
    const currentIndex = this.getCurrentIndex(chatItemIndex);
    this.metadataCarouselStates[chatItemIndex] = Math.max(0, currentIndex - this.itemsPerPage);
  }

  /**
   * Gets metadata items for current carousel page
   * 
   * @param metadata - Metadata object containing items
   * @param chatItemIndex - Index of the chat item
   * @returns any[] - Array of metadata items for current page
   */
  getCurrentPageItems(metadata: any, chatItemIndex: number): any[] {
    const allItems = this.getMetadataItems(metadata);
    const currentIndex = this.getCurrentIndex(chatItemIndex);
    const endIndex = currentIndex + this.itemsPerPage;
    return allItems.slice(currentIndex, endIndex);
  }

  /**
   * Gets current page metadata items with their original keys
   * Useful for maintaining reference to original metadata structure
   * 
   * @param metadata - Metadata object containing items
   * @param chatItemIndex - Index of the chat item
   * @returns Array of objects containing item and its key
   */
  getCurrentPageItemsWithKeys(metadata: any, chatItemIndex: number): { item: any, key: string }[] {
    try {
      if (!metadata || !metadata.answer || !metadata.answer.metadata) {
        return [];
      }

      const itemsWithKeys: { item: any, key: string }[] = [];
      const metadataObj = metadata.answer.metadata;

      // Extract numbered metadata items with their keys and filter out blocked items
      Object.keys(metadataObj).forEach(key => {
        if (!isNaN(Number(key))) { // Check if key is a number
          const item = metadataObj[key];
          // Only add if not blocked
          if (!this.isItemBlocked(item)) {
            itemsWithKeys.push({
              item: item,
              key: key
            });
          }
        }
      });

      // Apply pagination
      const currentIndex = this.getCurrentIndex(chatItemIndex);
      const endIndex = currentIndex + this.itemsPerPage;
      return itemsWithKeys.slice(currentIndex, endIndex);
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "getCurrentPageItemsWithKeys");
      return [];
    }
  }

  /**
   * Calculates end index for current carousel page
   * 
   * @param metadata - Metadata object containing items
   * @param chatItemIndex - Index of the chat item
   * @returns number - End index for current page
   */
  getEndIndex(metadata: any, chatItemIndex: number): number {
    const totalItems = this.getMetadataItems(metadata).length;
    const currentIndex = this.getCurrentIndex(chatItemIndex);
    return Math.min(currentIndex + this.itemsPerPage, totalItems);
  }

  /**
   * Determines if next button should be disabled for carousel
   * 
   * @param metadata - Metadata object containing items
   * @param chatItemIndex - Index of the chat item
   * @returns boolean - True if next button should be disabled
   */
  isNextDisabled(metadata: any, chatItemIndex: number): boolean {
    const totalItems = this.getMetadataItems(metadata).length;
    const currentIndex = this.getCurrentIndex(chatItemIndex);
    return currentIndex + this.itemsPerPage >= totalItems;
  }

  /**
   * Determines if previous button should be disabled for carousel
   * 
   * @param chatItemIndex - Index of the chat item
   * @returns boolean - True if previous button should be disabled
   */
  isPrevDisabled(chatItemIndex: number): boolean {
    const currentIndex = this.getCurrentIndex(chatItemIndex);
    return currentIndex === 0;
  }

  /**
   * Resets all carousel states when new chat data is loaded
   */
  resetCarousel(): void {
    this.metadataCarouselStates = {};
  }

  /**
   * Get CSS class for relevance score
   * @param relevanceScore - The relevance score (high, medium, low)
   * @returns CSS class string
   */
  public getRelevanceClass(relevanceScore: string): string {
    if (!relevanceScore) return '';

    switch (relevanceScore.toLowerCase()) {
      case 'high':
        return 'high';
      case 'medium':
        return 'medium';
      case 'low':
        return 'low';
      default:
        return '';
    }
  }

  /**
   * Angular lifecycle hook - called after view checking
   * Updates UI elements after view changes:
   * - Updates markdown links to open in new tabs
   * - Applies appropriate CSS classes to tables
   * - Handles LaTeX equation rendering and styling
   * 
   * Optimized to only run when content is actually loaded and not yet processed
   * to prevent flickering caused by continuous DOM manipulation
   * 
   * Note: After initial processing, individual element checks (data-* attributes)
   * ensure idempotent behavior even if this runs multiple times during async rendering
   */
  ngAfterViewChecked() {
    // Only process content once after it's loaded to prevent flickering
    if (!this.loading && this.chatData.length > 0 && !this.contentProcessed) {
      // Process immediately available content
      this.updateMarkdownLinks();
      this.updateTableClasses();
      
      // Add a small delay to ensure LaTeX equations are fully rendered
      setTimeout(() => {
        this.updateLatexScrollbars();
        this.forceFractionLineVisibility();
        
        // Mark content as processed to prevent repeated execution
        this.contentProcessed = true;
        
        // One final check after an additional delay for any async LaTeX rendering
        setTimeout(() => {
          this.updateLatexScrollbars();
          this.forceFractionLineVisibility();
        }, 200);
      }, 100);
    }
  }

  /**
   * Updates all markdown links to open in new tabs for external URLs
   * Also formats text-based bullet points into proper HTML lists
   * Called after view updates to handle dynamic content changes
   * 
   * Optimized to skip already-processed elements to prevent flickering
   */
  public updateMarkdownLinks(): void {
    try {
      // Find all markdown containers
      const markdownElements = this.elementRef.nativeElement.querySelectorAll('markdown, .markdown-content');

      markdownElements.forEach((markdownEl: HTMLElement) => {
        // Skip if already processed (marked with data attribute)
        if (markdownEl.hasAttribute('data-links-processed')) {
          return;
        }

        // First, handle text-based bullet points
        this.formatTextBasedLists(markdownEl);

        // Then handle external links
        const links = markdownEl.querySelectorAll('a[href]');
        links.forEach((link: any) => {
          const href = link.getAttribute('href');

          // Check if it's an external link (starts with http:// or https://)
          if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
            // Only update if not already set to avoid overriding
            if (link.getAttribute('target') !== '_blank') {
              link.setAttribute('target', '_blank');
              link.setAttribute('rel', 'noopener noreferrer');

              // Add security attributes for external links
              link.setAttribute('referrerpolicy', 'no-referrer');
            }
          }
        });

        // Mark this element as processed
        markdownEl.setAttribute('data-links-processed', 'true');
      });
    } catch (error) {
      this.logger.logError(this.AgentShareChatViewerError(error), "updateMarkdownLinks");
    }
  }

  /**
   * Updates CSS classes for tables based on column count
   * Tables with more than 5 columns receive 'wide-table' class for better styling
   * 
   * Optimized to skip already-processed tables to prevent flickering
   */
  public updateTableClasses(): void {
    try {
      const tables = this.elementRef.nativeElement.querySelectorAll('.markdown-content table');
      tables.forEach((table: HTMLTableElement) => {
        // Rich tables manage their own layout (resize, pagination)
        if (table.closest('[data-rich-table="true"]')) {
          return;
        }

        // Skip if already processed
        if (table.hasAttribute('data-table-processed')) {
          return;
        }

        const columnCount = table.querySelector('tr')?.querySelectorAll('th, td').length || 0;
        if (columnCount > 5) {
          this.renderer.addClass(table, 'wide-table');
        } else {
          this.renderer.removeClass(table, 'wide-table');
        }

        // Mark table as processed
        table.setAttribute('data-table-processed', 'true');
      });
    } catch (error) {
      this.logger.logError(this.AgentShareChatViewerError(error), "updateTableClasses");
    }
  }

  /**
   * Dynamically applies 'wide-equation' class to LaTeX equations that exceed 80% of container width
   * Handles both display and inline LaTeX equations for proper scrolling behavior
   * 
   * Optimized to skip already-processed equations to prevent flickering
   * 
   * @private
   */
  private updateLatexScrollbars(): void {
    try {
      const latexEquations = this.elementRef.nativeElement.querySelectorAll('.markdown-content .katex-display');

      latexEquations.forEach((equation: HTMLElement) => {
        // Skip if already processed
        if (equation.hasAttribute('data-latex-processed')) {
          return;
        }

        const container = equation.closest('.table-scroll-wrapper');
        if (container) {
          const containerWidth = container.clientWidth;
          const equationWidth = equation.scrollWidth;
          const widthPercentage = (equationWidth / containerWidth) * 100;

          if (widthPercentage > 80) {
            this.renderer.addClass(equation, 'wide-equation');
          } else {
            this.renderer.removeClass(equation, 'wide-equation');
          }
        }

        // Mark as processed
        equation.setAttribute('data-latex-processed', 'true');
      });

      // Also check inline LaTeX equations
      const inlineLatexEquations = this.elementRef.nativeElement.querySelectorAll('.markdown-content .katex:not(.katex-display)');

      inlineLatexEquations.forEach((equation: HTMLElement) => {
        // Skip if already processed
        if (equation.hasAttribute('data-latex-processed')) {
          return;
        }

        const container = equation.closest('.table-scroll-wrapper');
        if (container) {
          const containerWidth = container.clientWidth;
          const equationWidth = equation.scrollWidth;
          const widthPercentage = (equationWidth / containerWidth) * 100;

          if (widthPercentage > 80) {
            this.renderer.addClass(equation, 'wide-equation');
          } else {
            this.renderer.removeClass(equation, 'wide-equation');
          }
        }

        // Mark as processed
        equation.setAttribute('data-latex-processed', 'true');
      });
    } catch (error) {
      this.logger.logError(this.AgentShareChatViewerError(error), "updateLatexScrollbars");
    }
  }

  /**
   * Forces fraction lines to be visible in LaTeX equations by adding CSS classes
   * Handles cases where fraction lines may not render properly
   * Creates artificial fraction lines when necessary
   * 
   * Optimized to skip already-processed fractions to prevent flickering
   * 
   * @private
   */
  private forceFractionLineVisibility(): void {
    try {
      const fractionElements = this.elementRef.nativeElement.querySelectorAll('.markdown-content .katex .mfrac');

      fractionElements.forEach((fraction: HTMLElement) => {
        // Skip if already processed
        if (fraction.hasAttribute('data-fraction-processed')) {
          return;
        }

        // Add CSS class for fraction styling
        this.renderer.addClass(fraction, 'fraction-fixed');

        // Find and style the fraction line - try multiple selectors
        let fracLine = fraction.querySelector('.frac-line') as HTMLElement;
        if (!fracLine) {
          // Try alternative selectors
          fracLine = fraction.querySelector('.mfrac .frac-line') as HTMLElement;
        }
        if (!fracLine) {
          // Try to find any element that might be the fraction line
          fracLine = fraction.querySelector('[class*="frac"]') as HTMLElement;
        }

        if (fracLine) {
          // Add CSS class for fraction line styling
          this.renderer.addClass(fracLine, 'fraction-line-fixed');
        } else {
          // Try to create a fraction line if it doesn't exist
          const numerator = fraction.querySelector('.mfrac .mord, .mfrac .mbin, .mfrac .mrel');
          const denominator = fraction.querySelector('.mfrac .mord:last-child, .mfrac .mbin:last-child, .mfrac .mrel:last-child');

          if (numerator && denominator) {
            const artificialLine = document.createElement('div');
            artificialLine.className = 'artificial-frac-line fraction-line-fixed';

            // Insert between numerator and denominator
            if (denominator.parentNode) {
              denominator.parentNode.insertBefore(artificialLine, denominator);
            }
          }
        }

        // Mark fraction as processed
        fraction.setAttribute('data-fraction-processed', 'true');
      });
    } catch (error) {
      this.logger.logError(this.AgentShareChatViewerError(error), "forceFractionLineVisibility");
    }
  }

  /**
   * Converts text-based bullet points to proper HTML structure
   * Handles both unprocessed paragraphs and ngx-markdown processed content
   * Merges separate UL elements for proper nesting
   * 
   * @param container - HTML element containing the content to format
   */
  public formatTextBasedLists(container: HTMLElement): void {
    try {
      // First, handle the text-based formatting for paragraphs that haven't been processed by ngx-markdown
      this.formatParagraphLists(container);

      // Then, handle already processed UL elements that need to be nested
      this.mergeAdjacentLists(container);

    } catch (error) {
      this.logger.logError(this.AgentShareChatViewerError(error), "formatTextBasedLists");
    }
  }

  /**
   * Processes text-based bullet points in paragraph elements
   * Converts bullet characters (•) to proper HTML list structure
   * 
   * @param container - HTML element containing paragraphs to process
   */
  public formatParagraphLists(container: HTMLElement): void {
    try {
      const paragraphs = container.querySelectorAll('p');

      paragraphs.forEach((p: HTMLElement) => {
        const text = p.innerHTML;

        // Check if paragraph contains bullet points
        if (text.includes('•') || text.includes('\n•')) {
          // Split the text by lines and process
          const lines = text.split('\n');
          let htmlContent = '';
          let inMainList = false;

          lines.forEach((line) => {
            const trimmedLine = line.trim();

            if (!trimmedLine) {
              return;
            }

            if (trimmedLine.startsWith('•')) {
              // Start main list if not already started
              if (!inMainList) {
                htmlContent += '<ul class="formatted-list">';
                inMainList = true;
              }

              const content = trimmedLine.substring(1).trim();
              htmlContent += `<li>${content}</li>`;
            }
            else if (trimmedLine) {
              // Regular text - close lists
              if (inMainList) {
                htmlContent += '</ul>';
                inMainList = false;
              }
              htmlContent += `<p>${trimmedLine}</p>`;
            }
          });

          // Close any remaining open lists
          if (inMainList) {
            htmlContent += '</ul>';
          }

          // Replace the paragraph content if we found list items
          if (htmlContent && htmlContent.includes('<ul>')) {
            p.outerHTML = htmlContent;
          }
        }
      });
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "formatParagraphLists");
    }
  }

  /**
   * Merges paragraph elements containing bullets ending with ":" with following UL elements
   * Handles ngx-markdown processed content by creating proper nested list structures
   * 
   * @param container - HTML element containing the content to process
   */
  public mergeAdjacentLists(container: HTMLElement): void {
    try {
      // Get direct children only, not all nested elements
      const directChildren = Array.from(container.children) as HTMLElement[];

      for (let i = 0; i < directChildren.length - 1; i++) {
        const currentElement = directChildren[i];
        const nextElement = directChildren[i + 1];

        // Check if current element is a P with bullets and text ending in ":"
        if (currentElement.tagName === 'P' && nextElement.tagName === 'UL') {
          const pText = currentElement.textContent?.trim() || '';
          const pHtml = currentElement.innerHTML;

          // Check if paragraph contains bullets and ends with ":"
          if (pHtml.includes('•') && pText.endsWith(':')) {
            // Convert the paragraph bullets to a proper UL structure
            const lines = pHtml.split('\n');
            let newUlHtml = '<ul class="formatted-list">';

            lines.forEach((line) => {
              const trimmedLine = line.trim();
              if (trimmedLine.startsWith('•')) {
                const content = trimmedLine.substring(1).trim();
                newUlHtml += `<li>${content}</li>`;
              } else if (trimmedLine && !trimmedLine.startsWith('•')) {
                // Handle non-bullet lines (like headers with emojis)
                newUlHtml += `</ul><p>${trimmedLine}</p><ul class=\"formatted-list\">`;
              }
            });

            newUlHtml += '</ul>';

            // Add class to the following UL
            nextElement.classList.add('formatted-sublist');

            // Create the new structure
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = newUlHtml;
            const newUl = tempDiv.querySelector('ul') as HTMLElement;

            if (newUl) {
              // Find the last li in our new UL (should be the one ending with ":")
              const lastLi = newUl.querySelector('li:last-child');
              if (lastLi && lastLi.textContent?.trim().endsWith(':')) {
                // Move the next UL inside this li
                lastLi.appendChild(nextElement);
              }

              // Replace the original P element with our new structure
              currentElement.replaceWith(newUl);

              break; // Process one at a time to avoid issues with changing DOM
            }
          }
        }
      }
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "mergeAdjacentLists");
    }
  }

  /**
   * Handles tab switching functionality for response content
   * Manages active states for tabs and their corresponding content panels
   * 
   * @param tabName - Name of the tab to switch to
   * @param event - Click event from the tab element
   */
  switchTab(tabName: string, event: Event): void {
    try {
      event.preventDefault();

      // Get the current response container
      const currentResponseContainer = (event.target as HTMLElement).closest('.response-info-text');
      if (!currentResponseContainer) return;

      // Remove active class from all tabs and content within this specific response container
      const tabItems = currentResponseContainer.querySelectorAll('.tab-item');
      const tabContents = currentResponseContainer.querySelectorAll('.tab-content');

      tabItems.forEach(item => item.classList.remove('active'));
      tabContents.forEach(content => content.classList.remove('active'));

      // Add active class to clicked tab
      const clickedTab = (event.target as HTMLElement).closest('.tab-item');
      if (clickedTab) {
        clickedTab.classList.add('active');
      }

      // Show corresponding content within the same response container
      const targetContent = currentResponseContainer.querySelector(`#${tabName}-tab`);
      if (targetContent) {
        targetContent.classList.add('active');
      }

      // Trigger change detection to ensure UI updates
      this.cdr.detectChanges();
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "switchTab");
    }
  }

  /**
   * Handles clicks on news links by opening them in a new tab
   * 
   * @param url - URL of the news article to open
   */
  onNewsLinkClick(url: string): void {
    // Open the link in a new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Extracts image visualization data from metadata string
   * Processes base64 encoded images and returns them as a concatenated string
   * 
   * @param metadata - JSON string containing metadata with image visualization data
   * @returns Promise<any> - Concatenated base64 image strings or empty array
   */
  async extractImageVisualization(metadata: string): Promise<any> {
    if (!metadata) return [];

    try {
      const parsedMetadata = JSON.parse(metadata);
      const images = parsedMetadata?.answer?.image_visualization;
      if (Array.isArray(images)) {
        // Collect all non-empty base64 strings and join with a space
        return images
          .map((item: any) => item?.png_base64?.trim())
          .filter((base64: string) => !!base64)
          .join(" ");
      }
      return [];
    } catch (e) {
      this.logger.logError(this.AgentShareChatViewerError(e), "extractImageVisualization");
      return [];
    }
  }

  /**
   * Extracts module name from metadata item
   * Tries new API structure first, falls back to legacy extraction method
   * 
   * @param item - Metadata item to extract module name from
   * @returns string | null - Module name or null if not found
   */
  getModuleName(item: any): string | null {
    // First try to get module_name directly from the item (new API structure)
    if (item?.['module_name']) {
      return item['module_name'];
    }

    // Fallback to extractModuleName for backwards compatibility
    return this.metadataConfigService.extractModuleName(item);
  }

  /**
   * Retrieves field value from metadata item using configuration service
   * 
   * @param item - Metadata item to extract field from
   * @param fieldKey - Key of the field to retrieve
   * @returns any - Field value or undefined if not found
   */
  getFieldValue(item: any, fieldKey: string): any {
    return this.metadataConfigService.getFieldValue(item, fieldKey);
  }

  /** Max document rows shown before "+N more documents" in share metadata cards. */
  readonly DOC_TABLE_PREVIEW_COUNT = 3;
  readonly DOC_TABLE_PLACEHOLDER = DOC_TABLE_CELL_PLACEHOLDER;
  private readonly expandedDocumentKeys = new Set<string>();

  getDocumentRows(item: any): RegulationDocumentRow[] {
    return getRegulationDocumentRows(item);
  }

  getAttachments(item: any, fieldKey: string): string[] {
    void fieldKey;
    return this.getDocumentRows(item).map((row) => row.fileName);
  }

  getVisibleDocumentRows(itemKey: string, rows: RegulationDocumentRow[]): RegulationDocumentRow[] {
    if (!rows?.length) {
      return [];
    }
    if (this.isDocumentsExpanded(itemKey) || rows.length <= this.DOC_TABLE_PREVIEW_COUNT) {
      return rows;
    }
    return rows.slice(0, this.DOC_TABLE_PREVIEW_COUNT);
  }

  getVisibleAttachments(itemKey: string, attachments: string[]): string[] {
    if (!attachments?.length) {
      return [];
    }
    if (this.isDocumentsExpanded(itemKey) || attachments.length <= this.DOC_TABLE_PREVIEW_COUNT) {
      return attachments;
    }
    return attachments.slice(0, this.DOC_TABLE_PREVIEW_COUNT);
  }

  isDocumentsExpanded(itemKey: string): boolean {
    return this.expandedDocumentKeys.has(itemKey);
  }

  toggleDocumentsExpanded(itemKey: string): void {
    if (this.expandedDocumentKeys.has(itemKey)) {
      this.expandedDocumentKeys.delete(itemKey);
    } else {
      this.expandedDocumentKeys.add(itemKey);
    }
    this.cdr.markForCheck();
  }

  trackByDocumentRow(index: number, row: RegulationDocumentRow): string {
    return row?.fileName || String(index);
  }

  trackByAttachment(index: number, attach: string): string {
    return attach || String(index);
  }

  getAttachmentDisplayName(fileName: string): string {
    return formatAttachmentDisplayName(fileName);
  }

  formatDocTableCell(value: string): string {
    return formatDocTableCell(value, this.DOC_TABLE_PLACEHOLDER);
  }

  formatDocReleaseDate(value: string): string {
    return formatDocReleaseDate(value, this.DOC_TABLE_PLACEHOLDER);
  }

  /**
   * Checks if a field has a non-empty value in the metadata item
   * 
   * @param item - Metadata item to check
   * @param fieldKey - Key of the field to check
   * @returns boolean - True if field has a value, false otherwise
   */
  hasFieldValue(item: any, fieldKey: string): boolean {
    return this.metadataConfigService.hasFieldValue(item, fieldKey);
  }

  /**
   * Gets formatted field value according to field configuration
   * 
   * @param item - Metadata item containing the field
   * @param field - Field configuration object
   * @returns any - Formatted field value
   */
  getFormattedFieldValue(item: any, field: MetadataFieldConfig): any {
    const value = this.getFieldValue(item, field.key);
    return this.metadataConfigService.formatFieldValueEnhanced(value, field);
  }

  /**
   * Gets visible fields for compact view based on module type
   * 
   * @param item - Metadata item to analyze
   * @returns MetadataFieldConfig[] - Array of field configurations for compact display
   */
  getCompactFields(item: any): MetadataFieldConfig[] {
    const moduleName = this.getModuleName(item);
    return this.metadataConfigService.discoverAvailableFields(item, 'compact', moduleName || undefined);
  }

  /**
   * Gets display title from metadata item with backwards compatibility
   * Checks multiple possible title fields in order of preference
   * 
   * @param item - Metadata item to extract title from
   * @returns string - Display title or empty string if not found
   */
  getDisplayTitle(item: any): string {
    // Check multiple possible title fields in order of preference
    const titleFields = [
      'metadata.title',
      'title',
      'metadata.query_title',
      'query_title'
    ];

    for (const field of titleFields) {
      const value = this.getFieldValue(item, field);
      if (value && value.trim()) {
        return value;
      }
    }

    return '';
  }

  /**
   * Gets detailed fields configuration for metadata item
   * 
   * @param item - Metadata item to analyze
   * @returns MetadataFieldConfig[] - Array of field configurations for detailed display
   */
  getDetailedFields(item: any): MetadataFieldConfig[] {
    const moduleName = this.getModuleName(item);
    return this.metadataConfigService.discoverAvailableFields(item, 'detailed', moduleName || undefined);
  }

  /**
   * Gets display ID from metadata item with backwards compatibility
   * Checks multiple possible ID fields in order of preference
   * 
   * @param item - Metadata item to extract ID from
   * @returns string - Display ID or empty string if not found
   */
  getDisplayId(item: any): string {
    // Check multiple possible ID fields in order of preference
    const idFields = [
      'id',
      'ins_id',
      'rtq_id',
      'metadata.encrypted_regulation_id',
      'metadata.reference_insight_id'
    ];

    for (const field of idFields) {
      const value = this.getFieldValue(item, field);
      if (value && value.trim()) {
        return value;
      }
    }

    return '';
  }

}
